/**
 * AddPupilFlow — single source of truth for adding + inviting a pupil.
 *
 * Replaces the previous inline add-student form on WorkMode + InstructorDashboard.
 * Three sequential steps in one modal:
 *   1. DETAILS  — name, phone, email (email optional but recommended for invite)
 *   2. SAVED    — confirms creation + flips into the invite picker
 *   3. INVITE   — instructor picks SMS / WhatsApp / Email / Copy Link.
 *                 Native sharer opens with prefilled text incl. invite URL + PIN.
 *
 * Owner principle (Feb 28, 2026):
 *   "Adding a pupil without inviting them is incomplete."
 *
 * Status badges (Sent / Opened / Joined) live on the pupil list — see
 * <PupilInviteStatusBadge /> exported below.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, User, Phone, Mail, Loader2, MessageSquare, MessageCircle,
  Send, Link2, CheckCircle2, ExternalLink, ChevronLeft, Repeat,
  AlertCircle, UserCheck, Clock, ContactRound, MapPin,
} from "lucide-react";
import { MobileDialog } from "../mobile";

const API = process.env.REACT_APP_BACKEND_URL;
const MEMBERSHIP_INFO_KEY = "ndg_pupil_membership_info_seen_v1";

const STEPS = { DETAILS: "details", SAVED: "saved", INVITE: "invite" };

/**
 * MembershipInfoOnce — Feb 6, 2026.
 *
 * One-time informational notice shown the first time an instructor
 * successfully adds a pupil. Tells them about the £2/month passive
 * income from any pupil who chooses Membership. Per owner spec:
 *   • No accept / decline
 *   • No links / codes
 *   • No repetition after first view
 *
 * Dismissal is stored in localStorage so a returning user never
 * sees it twice on the same browser. There is no server flag — this
 * is purely UI noise control, not an audit trail.
 */
function MembershipInfoOnce() {
  const [seen, setSeen] = useState(() => {
    try { return !!localStorage.getItem(MEMBERSHIP_INFO_KEY); }
    catch { return false; }
  });
  if (seen) return null;
  const onClose = () => {
    try { localStorage.setItem(MEMBERSHIP_INFO_KEY, "1"); } catch {}
    setSeen(true);
  };
  return (
    <div
      data-testid="add-pupil-membership-info"
      className="rounded-2xl bg-emerald-50/60 border border-emerald-200 px-4 py-3 flex items-start gap-3"
    >
      <Repeat className="w-4 h-4 text-emerald-600 flex-none mt-0.5" />
      <p className="text-xs text-emerald-900 leading-snug flex-1">
        If your pupil chooses Membership, you'll receive £2/month from their plan.
        No action needed.
      </p>
      <button
        data-testid="add-pupil-membership-info-dismiss"
        onClick={onClose}
        className="text-emerald-700/70 hover:text-emerald-900 -mt-1 -mr-1 p-1"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function AddPupilFlow({ open, onClose, onCreated }) {
  const [step, setStep] = useState(STEPS.DETAILS);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [primaryAddress, setPrimaryAddress] = useState("");
  const [secondAddress, setSecondAddress] = useState("");
  const [thirdAddress, setThirdAddress] = useState("");
  const [defaultPrice, setDefaultPrice] = useState("");
  const [hoursCredit, setHoursCredit] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pupil, setPupil] = useState(null);   // { id, name, ... }
  const [invite, setInvite] = useState(null); // { invite_code, invite_pin, invite_url, share_messages }
  // Phase A · Invite continuity (Feb 2026) — read-only preflight.
  // Holds the latest preflight response so the modal can render a
  // calm continuity card instead of letting the legacy create+invite
  // chain dead-end on the duplicate-phone fraud guard.
  // See /app/memory/INVITE_CONTINUITY_AUDIT.md.
  const [preflight, setPreflight] = useState(null); // { case, student?, learner? }
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [contactImporting, setContactImporting] = useState(false);
  const [contactImportResult, setContactImportResult] = useState(null);
  const [contactCandidates, setContactCandidates] = useState([]);
  const [selectedContactIndexes, setSelectedContactIndexes] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [contactConfirming, setContactConfirming] = useState(false);
  const isAppleMobile =
    typeof navigator !== "undefined" &&
    (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

  function reset() {
    setStep(STEPS.DETAILS);
    setName(""); setPhone(""); setEmail("");
    setPrimaryAddress(""); setSecondAddress(""); setThirdAddress("");
    setDefaultPrice(""); setHoursCredit("");
    setSubmitting(false); setPupil(null); setInvite(null);
    setPreflight(null); setPreflightLoading(false);
    setContactImporting(false); setContactImportResult(null);
    setContactCandidates([]); setSelectedContactIndexes([]);
    setContactSearch(""); setContactConfirming(false);
  }

  function handleClose() {
    reset();
    onClose?.();
  }

  const formatContactAddress = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value.trim();
    return [
      value.addressLine?.join?.(", "),
      value.city,
      value.region,
      value.postalCode,
      value.country,
    ].filter(Boolean).join(", ");
  };

  async function importVCardFile(event) {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    setContactImporting(true);
    setContactImportResult(null);
    try {
      const cards = [];
      for (const file of files) {
        const raw = await file.text();
        const unfold = raw.replace(/\r?\n[ \t]/g, "");
        const blocks = unfold.match(/BEGIN:VCARD[\s\S]*?END:VCARD/gi) || [unfold];
        for (const block of blocks) {
          const valueFor = (key) => {
            const line = block.split(/\r?\n/).find((row) =>
              row.toUpperCase().startsWith(key) || row.toUpperCase().startsWith(`${key};`)
            );
            return line ? line.slice(line.indexOf(":") + 1).trim() : "";
          };
          const displayName = valueFor("FN") || valueFor("N").split(";").reverse().filter(Boolean).join(" ");
          if (!displayName) continue;
          cards.push({
            name: displayName,
            phone: valueFor("TEL").replace(/^tel:/i, ""),
            email: valueFor("EMAIL"),
            primary_address: valueFor("ADR").split(";").filter(Boolean).join(", "),
            temporary_addresses: [],
          });
        }
      }
      if (!cards.length) throw new Error("No named contacts found");
      setContactCandidates(cards);
      setSelectedContactIndexes([]);
      setContactSearch("");
      setContactConfirming(false);
      setContactImportResult({
        selected: 0,
        message: `${cards.length} contacts found. Search and select who you want — nobody has been added yet.`,
      });
    } catch (error) {
      setContactImportResult({ failed: files.length, message: error?.message || "Those contacts could not be read." });
    } finally {
      setContactImporting(false);
    }
  }

  async function importFromContacts() {
    // Safari can expose a contacts surface that opens the normal contact
    // viewer without returning a selected contact to the website. Do not
    // send instructors into that dead end; the NDG form remains available.
    if (isAppleMobile) {
      // iOS Safari does not expose the Contact Picker API. Use Apple's
      // supported contact-card handoff instead: share/save a contact as
      // a .vcf, then select it here.
      document.getElementById("pupil-vcard-input")?.click();
      return;
    }
    if (!navigator.contacts?.select) {
      setContactImportResult({
        unsupported: true,
        message: "This browser cannot open your phone contacts. Export the contacts as a CSV or vCard, then use Transfer pupils.",
      });
      return;
    }

    setContactImporting(true);
    setContactImportResult(null);
    try {
      const supported = navigator.contacts.getProperties
        ? await navigator.contacts.getProperties()
        : ["name", "tel", "email"];
      const properties = ["name", "tel", "email"];
      if (supported.includes("address")) properties.push("address");
      const contacts = await navigator.contacts.select(
        properties,
        { multiple: true },
      );
      if (!contacts?.length) {
        setContactImportResult({ cancelled: true, message: "No contacts selected." });
        return;
      }

      const candidates = contacts.map((contact) => ({
        name: contact.name?.[0]?.trim() || "Unnamed contact",
        phone: contact.tel?.[0]?.trim() || "",
        email: contact.email?.[0]?.trim() || undefined,
        primary_address: formatContactAddress(contact.address?.[0]) || undefined,
        temporary_addresses: [],
      }));
      setContactCandidates(candidates);
      setSelectedContactIndexes([]);
      setContactSearch("");
      setContactConfirming(false);
      setContactImportResult({
        selected: 0,
        message: `${candidates.length} contacts found. Select who you want — nobody has been added yet.`,
      });
    } catch (error) {
      if (error?.name === "InvalidStateError" || error?.name === "NotAllowedError") {
        setContactImportResult({ cancelled: true, message: "Contact selection was cancelled or not allowed." });
      } else {
        setContactImportResult({ failed: 1, message: "Contacts could not be transferred. Nothing was removed from your phone." });
      }
    } finally {
      setContactImporting(false);
    }
  }

  async function importSelectedContacts() {
    if (!selectedContactIndexes.length) {
      setContactImportResult({ message: "Select at least one contact first." });
      return;
    }
    if (!contactConfirming) {
      setContactConfirming(true);
      return;
    }
    setContactImporting(true);
    try {
      const selected = selectedContactIndexes.map((index) => contactCandidates[index]);
      const outcomes = await Promise.allSettled(selected.map((contact) =>
        axios.post(`${API}/api/work-mode/students`, contact, { withCredentials: true })
      ));
      const created = outcomes.filter((item) => item.status === "fulfilled").map((item) => item.value.data);
      const failed = selected.length - created.length;
      created.forEach((student) => onCreated?.(student));
      setContactCandidates([]);
      setSelectedContactIndexes([]);
      setContactConfirming(false);
      setContactImportResult({
        selected: selected.length, created: created.length, failed,
        message: `${created.length} selected pupil${created.length === 1 ? "" : "s"} added${failed ? `; ${failed} could not be added` : ""}.`,
      });
      if (created.length) toast.success(`${created.length} selected pupil${created.length === 1 ? "" : "s"} added`);
    } finally {
      setContactImporting(false);
    }
  }

  // Phase A · Invite continuity (Feb 2026) — debounced preflight on
  // phone/email change. READ-ONLY: never mutates state, never creates
  // a row. Surfaces case-tag so the modal can branch BEFORE create.
  useEffect(() => {
    const trimmedPhone = phone.trim();
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedName = name.trim().replace(/\s+/g, " ");
    // Name continuity matters too: older pupils may not have a phone/email
    // stored on the same record used by Work Mode.
    const hasPhone = trimmedPhone.length >= 7;
    const hasEmail = /.+@.+\..+/.test(trimmedEmail);
    const hasName = trimmedName.length >= 3;
    if (!hasPhone && !hasEmail && !hasName) {
      setPreflight(null);
      setPreflightLoading(false);
      return;
    }
    setPreflightLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (hasPhone) params.set("phone", trimmedPhone);
        if (hasEmail) params.set("email", trimmedEmail);
        if (hasName) params.set("name", trimmedName);
        const { data } = await axios.get(
          `${API}/api/work-mode/students/preflight?${params.toString()}`,
          { withCredentials: true },
        );
        setPreflight(data);
      } catch {
        // Soft-fail: preflight is best-effort guidance. Never block
        // the operator. The legacy savePupil() path remains the
        // backstop.
        setPreflight(null);
      } finally {
        setPreflightLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [name, phone, email]);

  // Phase A — Case A action: resend the existing invite using the
  // already-supported endpoint. No NEW mutation surface introduced.
  async function resendExistingInvite() {
    const sid = preflight?.student?.id;
    if (!sid) return;
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/api/work-mode/students/${sid}/invite`,
        {},
        { withCredentials: true },
      );
      // Treat this exactly like a fresh save — flip into the INVITE
      // step with the existing pupil + the (possibly refreshed) code.
      setPupil({
        id: sid,
        name: preflight.student.name,
        phone: preflight.student.phone,
        email: preflight.student.email,
      });
      setInvite(r.data);
      setStep(STEPS.INVITE);
      toast.success("Invite refreshed · new PIN ready");
    } catch (e) {
      toast.error("Couldn't refresh invite", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSubmitting(false);
    }
  }

  async function savePupil() {
    if (!name.trim()) {
      toast.error("Pupil name required");
      return;
    }
    if (!primaryAddress.trim()) {
      toast.error("Primary address required", {
        description: "NDG uses it as both the default pickup and return address.",
      });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/work-mode/students`,
        {
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim() || undefined,
          primary_address: primaryAddress.trim(),
          temporary_addresses: [secondAddress, thirdAddress]
            .map((value) => value.trim())
            .filter(Boolean),
          default_lesson_price: defaultPrice ? Number(defaultPrice) : undefined,
          hours_credit: hoursCredit ? Number(hoursCredit) : undefined,
        },
        { withCredentials: true },
      );
      setPupil(data);
      onCreated?.(data);

      // Creation and invitation are two distinct outcomes. The pupil may
      // already be safely stored even if invite generation has a temporary
      // failure; never leave the form looking as though Save did nothing.
      try {
        const r = await axios.post(
          `${API}/api/work-mode/students/${data.id}/invite`,
          {},
          { withCredentials: true },
        );
        setInvite(r.data);
        setStep(STEPS.INVITE);
        toast.success("Pupil saved · invite ready");
      } catch (inviteError) {
        toast.warning("Pupil saved · invite not generated", {
          description:
            inviteError?.response?.data?.detail ||
            "Open the pupil profile to send the invite later.",
        });
        handleClose();
      }
    } catch (e) {
      toast.error("Couldn't save pupil", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSubmitting(false);
    }
  }

  async function trackChannel(channel) {
    if (!pupil?.id) return;
    try {
      await axios.post(
        `${API}/api/work-mode/students/${pupil.id}/invite/track`,
        { channel },
        { withCredentials: true },
      );
    } catch { /* non-fatal */ }
  }

  function shareSMS() {
    if (!invite) return;
    trackChannel("sms");
    const body = encodeURIComponent(invite.share_messages.sms);
    const phoneTarget = (phone || "").replace(/\s+/g, "");
    // iOS uses sms:&body=, Android uses sms:?body=. The "&" form is the
    // universal-ish modern compromise — both modern clients accept it.
    window.location.href = `sms:${phoneTarget}?body=${body}`;
  }
  function shareWhatsApp() {
    if (!invite) return;
    trackChannel("whatsapp");
    const body = encodeURIComponent(invite.share_messages.whatsapp);
    const phoneTarget = (phone || "").replace(/[^\d+]/g, "");
    const target = phoneTarget ? `phone=${phoneTarget}&` : "";
    window.open(`https://wa.me/?${target}text=${body}`, "_blank");
  }
  function shareEmail() {
    if (!invite) return;
    trackChannel("email");
    const subject = encodeURIComponent(invite.share_messages.email_subject);
    const body = encodeURIComponent(invite.share_messages.email_body);
    const target = (email || "").trim();
    window.location.href = `mailto:${target}?subject=${subject}&body=${body}`;
  }
  async function copyLink() {
    if (!invite) return;
    try {
      await navigator.clipboard.writeText(
        `${invite.invite_url}\nPIN: ${invite.invite_pin}`,
      );
      trackChannel("copy_link");
      toast.success("Invite link + PIN copied");
    } catch {
      toast.error("Couldn't copy — long-press the link below to copy manually");
    }
  }

  if (!open) return null;

  return (
    <MobileDialog
      bareContent
      open={open}
      onClose={handleClose}
      testid="add-pupil-flow-modal"
    >
      <div
        className="w-full sm:max-w-md bg-white text-slate-900 rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <header className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-none">
          <div className="flex items-center gap-2">
            {step === STEPS.INVITE && (
              <button
                data-testid="invite-back-btn"
                onClick={() => setStep(STEPS.DETAILS)}
                className="text-slate-500 hover:text-slate-900 -ml-1 p-1"
                aria-label="Back to details"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <h2 className="text-lg font-extrabold text-slate-900">
              {step === STEPS.DETAILS ? "Add Pupil"
              : step === STEPS.SAVED   ? "Saving…"
              : "Invite your pupil"}
            </h2>
          </div>
          <button
            data-testid="add-pupil-close"
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-700 p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {step === STEPS.DETAILS && (
            <div className="p-4 space-y-2">
              <input
                id="pupil-vcard-input"
                type="file"
                accept=".vcf,text/vcard,text/x-vcard"
                multiple
                className="hidden"
                onChange={importVCardFile}
              />
              <button
                type="button"
                onClick={importFromContacts}
                disabled={contactImporting}
                className="w-full h-11 rounded-xl bg-blue-50 ring-1 ring-blue-200 text-blue-800 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {contactImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ContactRound className="w-4 h-4" />}
                Select one or multiple contacts
              </button>
              {contactImportResult?.message && (
                <p className="text-sm text-slate-600 px-1">{contactImportResult.message}</p>
              )}
              {contactCandidates.length > 0 && (
                <div className="rounded-xl border border-blue-200 bg-white p-3 space-y-3">
                  <input
                    value={contactSearch}
                    onChange={(event) => setContactSearch(event.target.value)}
                    placeholder="Search contacts"
                    className="w-full h-10 rounded-lg border border-slate-200 px-3 text-base"
                  />
                  <div className="flex items-center justify-between text-sm">
                    <button type="button" className="font-bold text-blue-700" onClick={() => {
                      const visible = contactCandidates
                        .map((contact, index) => ({ contact, index }))
                        .filter(({ contact }) => contact.name.toLowerCase().includes(contactSearch.toLowerCase()))
                        .map(({ index }) => index);
                      setSelectedContactIndexes(visible);
                      setContactConfirming(false);
                    }}>Select shown</button>
                    <span>{selectedContactIndexes.length} selected</span>
                    <button type="button" className="font-bold text-slate-600" onClick={() => {
                      setSelectedContactIndexes([]);
                      setContactConfirming(false);
                    }}>Clear</button>
                  </div>
                  <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                    {contactCandidates.map((contact, index) => ({ contact, index }))
                      .filter(({ contact }) => contact.name.toLowerCase().includes(contactSearch.toLowerCase()))
                      .map(({ contact, index }) => (
                        <label key={`${contact.name}-${index}`} className="flex items-center gap-3 py-3">
                          <input
                            type="checkbox"
                            checked={selectedContactIndexes.includes(index)}
                            onChange={() => {
                              setSelectedContactIndexes((current) =>
                                current.includes(index) ? current.filter((item) => item !== index) : [...current, index]
                              );
                              setContactConfirming(false);
                            }}
                            className="w-5 h-5"
                          />
                          <span className="min-w-0">
                            <span className="block font-bold text-slate-900 truncate">{contact.name}</span>
                            <span className="block text-xs text-slate-500 truncate">{contact.phone || contact.email || "No phone or email"}</span>
                          </span>
                        </label>
                      ))}
                  </div>
                  <button
                    type="button"
                    disabled={contactImporting || !selectedContactIndexes.length}
                    onClick={importSelectedContacts}
                    className={`w-full h-11 rounded-xl font-extrabold disabled:opacity-40 ${contactConfirming ? "bg-amber-500 text-slate-950" : "bg-blue-600 text-white"}`}
                  >
                    {contactConfirming
                      ? `Confirm import of ${selectedContactIndexes.length}`
                      : `Import ${selectedContactIndexes.length} selected`}
                  </button>
                  {contactConfirming && (
                    <p className="text-xs font-semibold text-amber-800 text-center">
                      Nothing is imported until you press confirm.
                    </p>
                  )}
                </div>
              )}
              <p className="text-sm text-slate-500 mb-2">
                After you save, we'll generate an invite link straight away so they know
                where to find the app — no chasing later.
              </p>

              <Field icon={User} label="Pupil name" required>
                <input
                  data-testid="pupil-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Sarah Jones"
                  className="w-full bg-transparent outline-none text-base font-semibold text-slate-900 caret-blue-600 placeholder:text-slate-400 placeholder:font-normal"
                  autoFocus
                />
              </Field>

              <Field icon={Phone} label="Mobile" hint="for SMS / WhatsApp invite">
                <input
                  data-testid="pupil-phone-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="07000 000 000"
                  inputMode="tel"
                  className="w-full bg-transparent outline-none text-base text-slate-900 caret-blue-600 placeholder:text-slate-400"
                />
              </Field>

              <Field icon={Mail} label="Email" hint="for email invite">
                <input
                  data-testid="pupil-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="sarah@example.com"
                  inputMode="email"
                  className="w-full bg-transparent outline-none text-base text-slate-900 caret-blue-600 placeholder:text-slate-400"
                />
              </Field>

              <Field icon={MapPin} label="Primary address" hint="default pickup & return" required>
                <textarea
                  data-testid="pupil-primary-address-input"
                  value={primaryAddress}
                  onChange={(e) => setPrimaryAddress(e.target.value)}
                  placeholder="House number, street, town, postcode"
                  rows={2}
                  className="w-full resize-none bg-transparent outline-none text-base text-slate-900 caret-blue-600 placeholder:text-slate-400"
                />
              </Field>

              <Field icon={MapPin} label="Temporary address 2" hint="overrides both for 24h">
                <input
                  data-testid="pupil-second-address-input"
                  value={secondAddress}
                  onChange={(e) => setSecondAddress(e.target.value)}
                  placeholder="Temporary pickup and return address"
                  className="w-full bg-transparent outline-none text-base text-slate-900 caret-blue-600 placeholder:text-slate-400"
                />
              </Field>

              <Field icon={MapPin} label="Temporary address 3" hint="overrides both for 24h">
                <input
                  data-testid="pupil-third-address-input"
                  value={thirdAddress}
                  onChange={(e) => setThirdAddress(e.target.value)}
                  placeholder="Another temporary pickup and return address"
                  className="w-full bg-transparent outline-none text-base text-slate-900 caret-blue-600 placeholder:text-slate-400"
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Lesson price" hint="optional">
                  <div className="flex items-center gap-1">
                    <span className="text-slate-500">£</span>
                    <input
                      data-testid="pupil-price-input"
                      value={defaultPrice}
                      onChange={(e) => setDefaultPrice(e.target.value.replace(/[^\d.]/g, ""))}
                      placeholder="40"
                      inputMode="decimal"
                      className="w-full bg-transparent outline-none text-base text-slate-900 caret-blue-600 placeholder:text-slate-400"
                    />
                  </div>
                </Field>
                <Field label="Hours credit" hint="optional">
                  <input
                    data-testid="pupil-hours-input"
                    value={hoursCredit}
                    onChange={(e) => setHoursCredit(e.target.value.replace(/[^\d.]/g, ""))}
                    placeholder="0"
                    inputMode="decimal"
                    className="w-full bg-transparent outline-none text-base text-slate-900 caret-blue-600 placeholder:text-slate-400"
                  />
                </Field>
              </div>

              {/* Phase A · Invite continuity card (Feb 2026) — read-only
                  continuity surface. Replaces the legacy dead-end toast
                  ("An invite has already been sent to this phone number")
                  with case-aware guidance. The Save button below is
                  hidden when a non-clear case is present so the operator
                  is steered into the right action instead of accidentally
                  creating an orphan row. */}
              <PreflightCard
                preflight={preflight}
                loading={preflightLoading}
                onResend={resendExistingInvite}
                submitting={submitting}
              />
            </div>
          )}

          {step === STEPS.INVITE && invite && (
            <div className="p-5 space-y-4">
              <div className="rounded-2xl bg-emerald-50 ring-1 ring-emerald-200 px-4 py-3 flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-none" />
                <div className="text-sm text-emerald-900">
                  <span className="font-bold">{pupil?.name}</span> added.
                  Now send their invite — pick a channel below.
                </div>
              </div>

              <MembershipInfoOnce />

              <p className="text-xs text-slate-500">
                Tapping a channel opens your phone's SMS / WhatsApp / Mail app with the
                message pre-typed. You can edit before sending.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <ChannelButton
                  testId="invite-share-sms"
                  icon={MessageSquare}
                  label="SMS"
                  hint={phone ? phone : "Phone needed"}
                  disabled={!phone}
                  onClick={shareSMS}
                  tone="indigo"
                />
                <ChannelButton
                  testId="invite-share-whatsapp"
                  icon={MessageCircle}
                  label="WhatsApp"
                  hint={phone ? phone : "Phone needed"}
                  disabled={!phone}
                  onClick={shareWhatsApp}
                  tone="emerald"
                />
                <ChannelButton
                  testId="invite-share-email"
                  icon={Send}
                  label="Email"
                  hint={email ? email : "Email needed"}
                  disabled={!email}
                  onClick={shareEmail}
                  tone="sky"
                />
                <ChannelButton
                  testId="invite-share-copy"
                  icon={Link2}
                  label="Copy link"
                  hint="Paste anywhere"
                  onClick={copyLink}
                  tone="slate"
                />
              </div>

              <div className="rounded-2xl bg-slate-100 px-4 py-3 space-y-1">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                  Their private link
                </div>
                <a
                  href={invite.invite_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="invite-url-preview"
                  className="text-xs text-blue-700 break-all inline-flex items-center gap-1 hover:underline"
                >
                  {invite.invite_url}
                  <ExternalLink className="w-3 h-3 flex-none" />
                </a>
                <div className="text-xs text-slate-700 mt-1">
                  PIN <span data-testid="invite-pin" className="font-mono font-bold tracking-widest text-slate-900">{invite.invite_pin}</span>
                </div>
              </div>

              <button
                data-testid="invite-done-btn"
                onClick={handleClose}
                className="w-full text-xs text-slate-500 hover:text-slate-800 mt-2"
              >
                I'll send it later — close
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === STEPS.DETAILS && (
          <footer className="px-5 py-4 border-t border-slate-200 flex-none">
            {/* Phase A — when a non-clear preflight case is showing the
                continuity card, suppress the Save button so the operator
                takes the case-appropriate action instead of accidentally
                creating an orphan work_students row. The continuity
                card's "Cancel" / "Edit details" affordances steer them
                back to a clear state. */}
            {preflight && preflight.case && preflight.case !== "clear" ? (
              <button
                data-testid="confirm-pupil-btn-disabled"
                disabled
                className="w-full h-12 rounded-2xl bg-slate-200 text-slate-500 font-extrabold inline-flex items-center justify-center gap-2 cursor-not-allowed"
              >
                Choose an action above
              </button>
            ) : (
              <button
                data-testid="confirm-pupil-btn"
                onClick={savePupil}
                disabled={!name.trim() || !primaryAddress.trim() || submitting}
                className="w-full h-12 rounded-2xl bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white font-extrabold inline-flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                ) : (
                  <>Save & invite <Send className="w-4 h-4" /></>
                )}
              </button>
            )}
          </footer>
        )}
      </div>
    </MobileDialog>
  );
}

function Field({ icon: Icon, label, hint, required, children }) {
  return (
    <label className="block rounded-xl bg-slate-50 ring-1 ring-slate-200 px-3 py-2">
      <div className="flex items-center gap-1 mb-1">
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
          {label}{required && <span className="text-rose-500"> *</span>}
        </span>
        {hint && <span className="text-[10px] text-slate-400 ml-auto italic">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function ChannelButton({ testId, icon: Icon, label, hint, onClick, disabled, tone = "indigo" }) {
  const TONES = {
    indigo:  "bg-indigo-50 ring-indigo-200 text-indigo-700 hover:bg-indigo-100",
    emerald: "bg-emerald-50 ring-emerald-200 text-emerald-700 hover:bg-emerald-100",
    sky:     "bg-sky-50 ring-sky-200 text-sky-700 hover:bg-sky-100",
    slate:   "bg-slate-50 ring-slate-200 text-slate-700 hover:bg-slate-100",
  };
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-2xl ring-1 px-4 py-4 text-left transition disabled:opacity-40 disabled:cursor-not-allowed ${TONES[tone]}`}
    >
      <Icon className="w-5 h-5 mb-2" />
      <div className="text-sm font-bold">{label}</div>
      <div className="text-[11px] opacity-80 truncate">{hint}</div>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Phase A · Invite continuity surface (Feb 2026)
// ═══════════════════════════════════════════════════════════════════
function PreflightCard({ preflight, loading, onResend, submitting }) {
  if (loading && !preflight) {
    return (
      <div
        data-testid="preflight-loading"
        className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 px-4 py-3 flex items-center gap-2 text-xs text-slate-500"
      >
        <Loader2 className="w-3 h-3 animate-spin" /> Checking for existing invite…
      </div>
    );
  }
  if (!preflight || preflight.case === "clear") return null;
  const inviteUrlFor = (code) => {
    if (!code) return null;
    const base = typeof window !== "undefined" ? window.location.origin : "";
    return `${base}/invite/${code}`;
  };
  switch (preflight.case) {
    case "pending_invite_same_instructor":
      return (
        <ContinuityCard testid="preflight-pending" tone="blue" icon={Clock}
          title="This learner already has a pending invite"
          body={<><span className="font-semibold">{preflight.student?.name}</span> has an invite waiting (PIN <span className="font-mono font-bold">{preflight.student?.invite_pin || "—"}</span>). Refresh it with a new PIN, copy the link, or open their profile.</>}
        >
          <ActionButton testid="preflight-resend-btn" onClick={onResend} disabled={submitting} primary>
            {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Refreshing…</>) : (<>Refresh invite & PIN</>)}
          </ActionButton>
          {preflight.student?.invite_code && (
            <ActionButton testid="preflight-copy-link-btn"
              onClick={async () => {
                const url = inviteUrlFor(preflight.student.invite_code);
                if (!url) return;
                try {
                  await navigator.clipboard.writeText(`${url}\nPIN: ${preflight.student?.invite_pin || ""}`);
                  toast.success("Invite link copied");
                } catch { toast.error("Couldn't copy — long-press the link"); }
              }}
            >Copy existing link</ActionButton>
          )}
          <OpenProfileLink id={preflight.student?.id} />
        </ContinuityCard>
      );
    case "stuck_same_instructor":
      return (
        <ContinuityCard testid="preflight-stuck" tone="amber" icon={AlertCircle}
          title="Onboarding stalled — needs a fresh invite"
          body={<><span className="font-semibold">{preflight.student?.name}</span> started onboarding but the account didn&apos;t finish creating. Sending a fresh invite issues a new code and PIN.</>}
        >
          <ActionButton testid="preflight-resend-btn" onClick={onResend} disabled={submitting} primary>
            {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>) : (<>Send a fresh invite</>)}
          </ActionButton>
          <OpenProfileLink id={preflight.student?.id} />
        </ContinuityCard>
      );
    case "linked_to_you":
      return (
        <ContinuityCard testid="preflight-linked-you" tone="emerald" icon={UserCheck}
          title="Already your pupil"
          body={<><span className="font-semibold">{preflight.student?.name}</span> is already linked to your account.</>}
        >
          <OpenProfileLink id={preflight.student?.id} primary />
        </ContinuityCard>
      );
    case "existing_account_unlinked":
      return (
        <ContinuityCard testid="preflight-account-exists" tone="sky" icon={UserCheck}
          title="This learner already has an NDG account"
          body={<><span className="font-semibold">{preflight.learner?.name || preflight.learner?.email}</span> is registered but not yet linked to you. They can connect once you send a connection request.</>}
        >
          <ActionButton testid="preflight-send-connection-btn-disabled" disabled hint="Coming soon">
            Send connection request
          </ActionButton>
        </ContinuityCard>
      );
    case "linked_to_another_instructor":
      return (
        <ContinuityCard testid="preflight-linked-elsewhere" tone="slate" icon={AlertCircle}
          title="This learner is currently working with another instructor"
          body={<>They&apos;re already onboarded elsewhere. Transfers and trust flows are coming soon — for now please reach out to them directly to confirm.</>}
        />
      );
    default:
      return null;
  }
}

function ContinuityCard({ testid, tone, icon: Icon, title, body, children }) {
  const TONES = {
    blue:    "bg-blue-50 ring-blue-200 text-blue-900",
    amber:   "bg-amber-50 ring-amber-200 text-amber-900",
    emerald: "bg-emerald-50 ring-emerald-200 text-emerald-900",
    sky:     "bg-sky-50 ring-sky-200 text-sky-900",
    slate:   "bg-slate-50 ring-slate-200 text-slate-900",
  };
  const ICONS = {
    blue:    "text-blue-600",
    amber:   "text-amber-600",
    emerald: "text-emerald-600",
    sky:     "text-sky-600",
    slate:   "text-slate-500",
  };
  return (
    <div data-testid={testid} className={`rounded-2xl ring-1 px-4 py-3 space-y-3 ${TONES[tone] || TONES.slate}`}>
      <div className="flex items-start gap-2">
        {Icon && <Icon className={`w-4 h-4 flex-none mt-0.5 ${ICONS[tone] || ICONS.slate}`} />}
        <div className="flex-1 space-y-1">
          <div className="text-sm font-bold">{title}</div>
          <div className="text-xs leading-snug opacity-90">{body}</div>
        </div>
      </div>
      {children && <div className="flex flex-col gap-2">{children}</div>}
    </div>
  );
}

function ActionButton({ testid, onClick, disabled, primary, hint, children }) {
  const base = "w-full h-10 rounded-xl text-sm font-bold inline-flex items-center justify-center gap-2 transition";
  const cls = primary
    ? `${base} bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40`
    : `${base} bg-white ring-1 ring-slate-300 text-slate-800 hover:bg-slate-100 disabled:opacity-40`;
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled} className={cls}>
      {children}
      {hint && <span className="text-[10px] opacity-70">· {hint}</span>}
    </button>
  );
}

function OpenProfileLink({ id, primary }) {
  if (!id) return null;
  return (
    <ActionButton testid="preflight-open-profile-btn"
      onClick={() => { window.location.href = `/pupil/${id}`; }}
      primary={primary}
    >Open profile</ActionButton>
  );
}

/**
 * PupilInviteStatusBadge — drop-in pill for pupil rows.
 *
 * Maps `work_students.invite_status` → calm-tone badge:
 *   none      → "No invite" (grey)        — needs attention
 *   invited   → "Sent · {channel}" (blue) — instructor sent, learner not opened
 *   opened    → "Opened" (amber)          — learner clicked, didn't finish onboarding
 *   joined    → "Joined" (emerald)        — PIN verified, onboarding started
 *   completed → "Onboarded" (emerald solid) — full setup done
 */
export function PupilInviteStatusBadge({ student }) {
  const status = student?.invite_status || "none";
  const channel = student?.invite_channel;
  const TONE = {
    none:      "bg-slate-100 text-slate-600 ring-slate-200",
    prepared:  "bg-slate-100 text-slate-600 ring-slate-200",
    invited:   "bg-blue-50 text-blue-700 ring-blue-200",
    opened:    "bg-amber-50 text-amber-700 ring-amber-200",
    joined:    "bg-emerald-50 text-emerald-700 ring-emerald-200",
    completed: "bg-emerald-600 text-white ring-emerald-700",
  }[status] || "bg-slate-100 text-slate-600 ring-slate-200";
  const LABEL = {
    none:      "No invite",
    prepared:  "Ready to share",
    // Surface-3 Truth Hardening (Feb 2026): "Sent" is reserved for
    // provider-confirmed authority. The `invited` state means the
    // instructor has SHARED the invite (via copy-link / SMS app /
    // email app); there is no Resend/Twilio webhook confirming the
    // pupil received it. Truth-safe wording: "Shared".
    invited:   student?.invite_sent_at
      ? (channel ? `Shared · ${channel}` : "Shared")
      : "Invite pending",
    opened:    "Opened",
    joined:    "Joined",
    completed: "Onboarded",
  }[status] || status;
  return (
    <span
      data-testid={`invite-status-${student?.id || ""}`}
      data-invite-status={status}
      className={`inline-flex items-center text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 ring-1 ${TONE}`}
    >
      {LABEL}
    </span>
  );
}
